/**
 * The selected edge's own fields, editable beside the canvas.
 *
 * A panel rather than inline editing on the edge label - which SysON also moved
 * away from - because a guard is an expression, and an expression is not
 * something to type into a label the size of a word.
 *
 * Which fields depends on the diagram: a transition has trigger, guard and
 * effect; a succession between steps has none, so its panel only deletes.
 */

import { useState } from 'react'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { Transition, TransitionEdit } from '../../schema'
import { useSchema } from '../../state/AppData'
import { MONOSPACE } from '../../theme'
import ElementPicker from '../common/ElementPicker'
import { useElements } from '../common/elements'
import { offeredAsDecider } from '../states/state-authoring'
import type { EdgeField } from './diagramKinds'

const FIELD_TEXT: Record<EdgeField, { label: string; help: string }> = {
  trigger: { label: 'Trigger', help: 'accept …' },
  guard: { label: 'Guard', help: 'if …' },
  effect: { label: 'Effect', help: 'do …' },
}

export default function TransitionPanel({
  transition,
  owner,
  noun = 'transition',
  fields = ['trigger', 'guard', 'effect'],
  decider = false,
  disabled,
  onSave,
  onDelete,
}: {
  transition: Transition
  /** The element whose body declares it. */
  owner: string
  /** What the edge is called: 'transition' or 'succession'. */
  noun?: string
  /** The fields the edge carries beyond its ends. */
  fields?: EdgeField[]
  /** Whether it says which part decides to take it - a transition does (issue #50). */
  decider?: boolean
  /** Why nothing here may be changed, or '' when it may. */
  disabled: string
  onSave: (patch: Partial<TransitionEdit>) => void
  onDelete: () => void
}) {
  const schema = useSchema()
  const parts = useElements(schema, decider ? 'parts' : undefined)
  const [values, setValues] = useState({
    trigger: transition.trigger,
    guard: transition.guard,
    effect: transition.effect,
  })
  const [decidedBy, setDecidedBy] = useState(transition.decided_by ?? '')
  const deciderBroken = parts.complete && Boolean(decidedBy)
    && !parts.rows.some((row) => row.id === decidedBy)
  const changed = fields.some((key) => values[key] !== transition[key])
    || (decider && decidedBy !== (transition.decided_by ?? ''))
  const end = (reference: string, resolved: string) => (reference || resolved).split('·').pop()

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack spacing={1}>
        <Typography variant="subtitle2">
          <span style={{ fontFamily: MONOSPACE }}>{end(transition.from_reference, transition.from)}</span>
          {' → '}
          <span style={{ fontFamily: MONOSPACE }}>{end(transition.to_reference, transition.to)}</span>
          <Typography component="span" variant="caption" color="text.secondary">
            {'  declared in '}
            {owner}
          </Typography>
        </Typography>
        {fields.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {fields.map((key) => (
              <TextField
                key={key}
                size="small"
                label={FIELD_TEXT[key].label}
                placeholder={FIELD_TEXT[key].help}
                value={values[key]}
                disabled={!!disabled}
                onChange={(event) => setValues((current) => ({ ...current, [key]: event.target.value }))}
                slotProps={{ htmlInput: { style: { fontFamily: MONOSPACE } } }}
              />
            ))}
          </Stack>
        )}
        {decider && (
          <ElementPicker
            rows={parts.rows}
            where={offeredAsDecider}
            strict={parts.complete ? 'part' : false}
            value={decidedBy || null}
            onChange={setDecidedBy}
            label="Decided by"
            disabled={!!disabled}
          />
        )}
        <Stack direction="row" spacing={1}>
          {fields.length > 0 && (
            <Button
              size="small"
              variant="contained"
              disabled={!!disabled || !changed || deciderBroken}
              onClick={() => onSave({
                ...Object.fromEntries(fields.map((key) => [key, values[key]])),
                ...(decider ? { decided_by: decidedBy } : {}),
              })}
            >
              Save {noun}
            </Button>
          )}
          <Button size="small" color="error" disabled={!!disabled} onClick={onDelete}>
            Delete {noun}
          </Button>
          {disabled && (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {disabled}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}
